import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import {
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
} from "@/payroll/pay-breakdown/types";
import type { PayBreakdownRunTarget } from "@/payroll/pay-breakdown/types";
import {
  getPayBreakdownCalculationState,
  getPayBreakdownVisibleRunAt,
  getPayBreakdownActiveRun,
  hasActiveAiRunAffectingVisibleSheets,
} from "@/payroll/pay-breakdown/utils";
import type { IcmOutputState } from "./use-icm-output";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type PayBreakdownViewStateParams = Pick<
  PayBreakdownPeriodState,
  | "output"
  | "selectedPayPeriod"
  | "periodDetail"
  | "selectedPeriod"
  | "selectedPeriodForCard"
  | "hasActiveRun"
  | "hasActiveIcmPayrollRun"
  | "hasActiveLegacyRun"
  | "activeAiScriptRuns"
  | "periodReady"
  | "shouldFetchLegacyDetail"
  | "isLegacyDetailLoading"
  | "selectedRunStatus"
> &
  Pick<IcmOutputState, "statements" | "isStatementsLoading"> & {
    runTarget: PayBreakdownRunTarget;
  };

export const getPayBreakdownViewState = ({
  output,
  selectedPayPeriod,
  periodDetail,
  selectedPeriod,
  selectedPeriodForCard,
  hasActiveRun,
  hasActiveIcmPayrollRun,
  hasActiveLegacyRun,
  activeAiScriptRuns,
  periodReady,
  shouldFetchLegacyDetail,
  isLegacyDetailLoading,
  selectedRunStatus,
  statements,
  isStatementsLoading,
  runTarget,
}: PayBreakdownViewStateParams) => {
  const visibleRunAt = getPayBreakdownVisibleRunAt(
    output,
    selectedPayPeriod,
    periodDetail,
  );
  const activeRun = getPayBreakdownActiveRun(selectedPayPeriod);
  const calculationState = getPayBreakdownCalculationState({
    hasSelectedPeriod: !!selectedPeriod,
    hasActiveRun,
    hasOutput: output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.none,
    activeRunAffectsVisibleOutput:
      hasActiveIcmPayrollRun ||
      hasActiveLegacyRun ||
      hasActiveAiRunAffectingVisibleSheets(activeAiScriptRuns),
  });
  const showInitialCalculationState =
    calculationState === PAY_BREAKDOWN_CALCULATION_STATE.initial;
  const showLegacyDetailLoading =
    periodReady && shouldFetchLegacyDetail && isLegacyDetailLoading;
  const showNoRunState =
    periodReady &&
    !isLegacyDetailLoading &&
    !hasActiveRun &&
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.none;
  const showFailedAiRun =
    showNoRunState &&
    selectedRunStatus === PAYROLL_RUN_STATUS.failed &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript;
  const showNoAiRunYet =
    showNoRunState &&
    selectedRunStatus !== PAYROLL_RUN_STATUS.failed &&
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript &&
    !!selectedPeriod;
  const isIcmTarget = runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm;
  const isLegacyTarget =
    runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript;
  const targetRunsCount = isIcmTarget
    ? selectedPeriodForCard?.icm_runs_count
    : selectedPeriodForCard?.runs_count;
  // ICM and script payroll share the run page until the period has a run;
  // after that, the period card shows the run's status and who requested it.
  const showNoCardRunYet =
    showNoRunState &&
    (isIcmTarget || isLegacyTarget) &&
    !!selectedPeriodForCard &&
    !targetRunsCount;
  const showNoIcmRunYet =
    showNoRunState &&
    isIcmTarget &&
    !!selectedPeriodForCard &&
    !showNoCardRunYet;
  const showNoLegacyRunYet =
    showNoRunState &&
    isLegacyTarget &&
    !!selectedPeriodForCard &&
    !showNoCardRunYet;
  const showIcmOutputLoading =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm && isStatementsLoading;
  const showIcmNoResults =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm &&
    !isStatementsLoading &&
    !!statements &&
    statements.length === 0;
  const showIcmOutput =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm &&
    !isStatementsLoading &&
    !!statements &&
    statements.length > 0;

  return {
    visibleRunAt,
    activeRun,
    calculationState,
    showInitialCalculationState,
    showLegacyDetailLoading,
    showFailedAiRun,
    showNoAiRunYet,
    showNoRunYet: showNoAiRunYet || showNoCardRunYet,
    showNoIcmRunYet,
    showNoLegacyRunYet,
    showIcmOutputLoading,
    showIcmNoResults,
    showIcmOutput,
  };
};
